import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Images, Package, GalleryHorizontal, Building2, MapPin,
  MessageSquareQuote, Inbox, Settings as SettingsIcon, LogOut, Menu, X, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/sliders", label: "Sliders", icon: Images },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/gallery", label: "Gallery", icon: GalleryHorizontal },
  { to: "/admin/distributors", label: "Distributors", icon: Building2 },
  { to: "/admin/machine-locations", label: "Machine Locations", icon: MapPin },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-600 transition-colors ${
      isActive ? "bg-brand text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
    }`;

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col bg-brand-navy">
      <div className="flex items-center justify-between px-5 py-5">
        <Link to="/admin" className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-white font-display text-lg font-800 text-brand">A</span>
          <span className="font-display text-lg font-800 text-white">AIMWAY</span>
        </Link>
        <button className="text-white lg:hidden" onClick={() => setOpen(false)}><X size={22} /></button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <a href="/" target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-600 text-slate-300 hover:bg-white/10 hover:text-white"><ExternalLink size={18} /> View Website</a>
        <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-600 text-slate-300 hover:bg-white/10 hover:text-white" data-testid="admin-logout"><LogOut size={18} /> Logout</button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3.5">
          <button className="lg:hidden text-brand-navy" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu size={24} /></button>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-700 text-brand-navy">{user?.name || "Admin"}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
            <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-sky font-display font-800 text-brand">{(user?.name || "A")[0]}</span>
          </div>
        </header>
        <main className="flex-1 p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
